import { useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { Search, X } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { useModalFocus } from "../ui/useModalFocus";
import { formatDay, formatTime } from "./format";
import { conversationLabel, type Conversation } from "./types";

type Props = { conversations: readonly Conversation[]; onClose: () => void; onOpen: (conversationId: string, threadRootId?: string | null) => void };

export function SearchPalette({ conversations, onClose, onOpen }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => { const timer = window.setTimeout(() => { setDebounced(text.trim()); }, 220); return () => { window.clearTimeout(timer); }; }, [text]);
  const hits = useQuery(api.communication.searchMessages, debounced.length > 0 ? { queryText: debounced } : "skip");
  const nameOf = (conversationId: string) => { const conversation = conversations.find((candidate) => candidate.id === conversationId); return conversation === undefined ? "conversation" : conversation.type === "direct" ? conversationLabel(conversation) : `#${conversationLabel(conversation)}`; };
  useModalFocus(panelRef, onClose, "input");

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="search-dialog" role="dialog" aria-modal="true" aria-label="Search messages" ref={panelRef}>
        <div className="search-input-row">
          <Search size={17} />
          <input name="search" autoComplete="off" value={text} onChange={(event) => { setText(event.target.value); }} placeholder="Search messages…" aria-label="Search messages" />
          <Button size="icon" variant="ghost" aria-label="Close search" onClick={onClose}><X size={16} /></Button>
        </div>
        {debounced.length === 0 ? (
          <div className="search-empty"><Search size={22} /><strong>Search every conversation you belong to</strong><p>Results only include rooms you are a member of.</p></div>
        ) : hits === undefined ? (
          <div className="search-empty"><p>Searching…</p></div>
        ) : hits.length === 0 ? (
          <div className="search-empty"><strong>No matches for “{debounced}”</strong><p>Try a different word.</p></div>
        ) : (
          <ul className="search-results">
            {hits.map((hit) => (
              <li key={hit.id}>
                <button type="button" onClick={() => { onOpen(hit.conversationId, hit.threadRootId ?? null); }}>
                  <div className="search-result__meta"><strong>{nameOf(hit.conversationId)}</strong><span>{formatDay(hit.createdAt)} · {formatTime(hit.createdAt)}{hit.threadRootId !== undefined ? " · in thread" : ""}</span></div>
                  <p>{hit.plainText.length > 180 ? `${hit.plainText.slice(0, 180)}…` : hit.plainText}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
